import React from 'react';
import { Button, Card, Checkbox, Col, ConfigProvider, Form, Row, Select } from 'antd';
import { useQuery } from 'react-query';
import { CargoContractStatusTypes, CargoContractTypes, CargoDeclaredTypes } from '@shared/utils/constants';
import { round } from '@shared/utils/math';
import { GetCargoDetailsResponse } from '@api/features/cargos/getCargoDetails';
import { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import { useAuth } from '@client/lib/auth';
import { OnUpdateCargo } from '@client/screens/estimates/details/context/hooks/useCargoHandlers';
import { getReportGroups, getVessels } from '@client/lib/api';
import NumericInput from '@client/components/NumericInput';
import MultiCurrencyInput from '@client/components/MultiCurrencyInput';
import { ModalModes } from './CargoDetailsScreen';
import styles from './CargoObligationDetailsForm.module.css';

type Props = {
	cargoDetails: GetCargoDetailsResponse | undefined;
	estimate: GetEstimateDetailsResponse | null;
	handleChange: OnUpdateCargo;
	mode: ModalModes;
};

const CargoObligationDetailsForm = ({
	cargoDetails,
	estimate,
	handleChange,
	mode,
}: Props) => {
	const { userOptions, userInfo } = useAuth();
	const { data: vessels } = useQuery('vessels', () => getVessels());
	const { data: reportGroups } = useQuery('reportGroups', () => getReportGroups());
	const { componentDisabled } = ConfigProvider.useConfig();

	if (cargoDetails == null) {
		return null;
	}

	const vesselOptions = (vessels ?? []).map((vessel) => ({
		value: vessel.id,
		label: vessel.name,
	}));

	const holdingReportGroupOptions = (reportGroups?.holdingReportGroups ?? [])
		.map((reportGroup) => ({
			value: reportGroup,
			label: reportGroup,
		}));

	const declaredOptions = Object.values(CargoDeclaredTypes).map((v) => ({
		value: v,
		label: v,
	}));

	const contractTypeOptions = Object.values(CargoContractTypes).map((v) => ({
		value: v,
		label: v,
	}));

	const handleImportEstimateValues = () => {
		handleChange(cargoDetails.id, {
			totalDays: round(estimate?.result.estimatedDuration ?? 0),
			tcePerDay: round(estimate?.result.tce ?? 0),
			tcePerDayCurrency: estimate?.currency,
			tcePerDayExchangeRate: 1,
		});
	};

	const isCargoLockedForEditing =
		componentDisabled ||
		cargoDetails.contractStatus === CargoContractStatusTypes.OnVoyage ||
		cargoDetails.contractStatus === CargoContractStatusTypes.Cancelled;

	return (
		<Card>
			<Form
				layout="horizontal"
				labelCol={{ flex: '125px' }}
				wrapperCol={{ flex: 1 }}
				labelAlign="right"
				colon
			>
				<Row gutter={[24, 0]}>
					<Col span={12}>
						<Form.Item label="Fixed by">
							<Select
								options={userOptions}
								value={cargoDetails.fixedBy}
								onChange={(fixedBy) => handleChange(cargoDetails.id, { fixedBy })}
								showSearch
							/>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="Part cargo">
							<Checkbox
								checked={cargoDetails.partCargo ?? false}
								onChange={({ target }) =>
									handleChange(cargoDetails.id, { partCargo: target.checked })
								}
								disabled={isCargoLockedForEditing}
							/>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="Declared">
							<Select
								options={declaredOptions}
								value={cargoDetails.declared}
								onChange={(declared) => handleChange(cargoDetails.id, { declared })}
								showSearch
								disabled={isCargoLockedForEditing}
							/>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="Holding groups">
							<Select
								mode="multiple"
								options={holdingReportGroupOptions}
								value={cargoDetails.holdingReportGroups ?? []}
								onChange={(holdingReportGroups) =>
									handleChange(cargoDetails.id, { holdingReportGroups })
								}
								showSearch
								allowClear
								placeholder="Select holding report groups"
								disabled={isCargoLockedForEditing}
							/>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="Contract type">
							<Select
								options={contractTypeOptions}
								value={cargoDetails.contractType}
								onChange={(contractType) =>
									handleChange(cargoDetails.id, { contractType })
								}
								showSearch
							/>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="Nominated vessel">
							<Select
								options={vesselOptions}
								value={cargoDetails.nominatedVesselId}
								onChange={(nominatedVesselId) =>
									handleChange(cargoDetails.id, { nominatedVesselId: nominatedVesselId ?? null })
								}
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="TCE/Day" className={styles.inlineFormItem}>
							<MultiCurrencyInput
								baseCurrency={userInfo.baseCurrency}
								inputValues={{
									value: cargoDetails.tcePerDay ?? 0,
									currency: cargoDetails.tcePerDayCurrency,
									exchangeRate: cargoDetails.tcePerDayExchangeRate,
								}}
								onChange={(v) => {
									handleChange(cargoDetails.id, {
										tcePerDay: v.value ?? cargoDetails.tcePerDay,
										tcePerDayCurrency: v.currency ?? cargoDetails.tcePerDayCurrency,
										tcePerDayExchangeRate:
											v.exchangeRate ?? cargoDetails.tcePerDayExchangeRate,
									});
								}}
								placeholder="TCE per day"
							/>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="Total days">
							<NumericInput
								value={round(cargoDetails.totalDays ?? 0)}
								onChange={(totalDays) =>
									handleChange(cargoDetails.id, { totalDays })
								}
								placeholder="Total days"
							/>
						</Form.Item>
					</Col>

					{mode === 'estimate' && (
						<Col span={12}>
							<Form.Item label=" ">
								<Button onClick={handleImportEstimateValues}>
									Import TCE/day and Total days
								</Button>
							</Form.Item>
						</Col>
					)}
				</Row>
			</Form>
		</Card>
	);
};

export default CargoObligationDetailsForm;